import es from 'dayjs/locale/es'
import { memo } from 'react'
import InputBase from '@material-ui/core/InputBase'
import DayjsAdapter from '@date-io/dayjs'
import {
  DateRangePicker,
  LocalizationProvider,
  DateRangeDelimiter
} from '@material-ui/pickers'
import styled from 'styled-components'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import { AdormentRight } from '../../Adorment'
import Text from '../../Typography/Text'

const Delimiter = styled(DateRangeDelimiter)`
  margin: 0px;
  margin-right: 8px;
  margin-left: 8px;
`

const DateText = styled(Text)`
  font-weight: bold;
  padding-left: 14px;
  margin-right: 14px;
`
const InputContainer = styled('div')`
  height: 36px;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: ${({ withborder }) =>
    withborder === 'true' ? '0px' : '5px'};
  border-bottom-left-radius: ${({ withborder }) =>
    withborder === 'true' ? '0px' : '5px'};
  border-top-right-radius: ${({ withborder }) =>
    withborder === 'true' ? '5px' : '0px'};
  border-bottom-right-radius: ${({ withborder }) =>
    withborder === 'true' ? '5px' : '0px'};
  max-width: auto;
  border: 1px solid #b7b7b7;
  box-sizing: border-box;
`

const InputWrapper = styled(props => <InputBase {...props} />)`
  height: 36px;
  width: 8rem;
  font-weight: 500;
  color: ${({ theme }) =>
    theme.palette.type === 'dark' ? '#FFFFFF' : '#202223'};
  @media (max-width: 700px) {
    width: 100%;
  }
`

const InputWrapperEnd = styled(InputWrapper)`
  width: 8rem;
  @media (max-width: 700px) {
    width: 100%;
  }
`

const InputWrapperStart = styled(InputWrapper)`
  width: ${({ withborder }) => (withborder === 'true' ? '12rem' : '9rem')};
  min-width: ${({ withborder }) =>
    withborder === 'true' ? '12rem' : '9rem'};
`

/**
 * Compares the `value` property of the previous and next props to determine equality.
 *
 * @param {Object} prevProps - The previous props object.
 * @param {Object} nextProps - The next props object.
 * @param {*} prevProps.value - The value from the previous props.
 * @param {*} nextProps.value - The value from the next props.
 * @returns {boolean} - Returns `true` if the `value` properties are equal, otherwise `false`.
 */
const isEqualValue = (prevProps, nextProps) =>
  prevProps?.value === nextProps?.value

/**
 * InputRangeDate is a memoized React component that renders a date range picker
 * with custom input fields and styling. It uses the Material-UI DateRangePicker
 * component and integrates with the Day.js date adapter for localization.
 *
 * @component
 * @param {Object} props - The props for the InputRangeDate component.
 * @param {string} props.prefixName - A prefix text displayed in the start date input field.
 * @param {Array} props.value - The selected date range value as an array of two dates.
 * @param {boolean} props.withborder - Determines whether the input fields should have a border.
 * @param {Object} props - The props for the InputRangeDate component.
 * @param {string} props.prefixName - A prefix text displayed in the start date input field.
 * @param {Array} props.value - The selected date range value as an array of two dates.
 * @param {boolean} props.withborder - Determines whether the input fields should have a border.
 * @param {...Object} props - Additional props passed to the DateRangePicker component.
 * @returns {JSX.Element} The rendered InputRangeDate component.
 */
const InputRangeDate = memo(
  ({ prefixName, value, withborder, ...props }) => (
    <LocalizationProvider dateAdapter={DayjsAdapter} locale={es}>
      <DateRangePicker
        {...props}
        value={value}
        disableFuture
        variant="inline"
        invalidDateMessage="Formato inválido"
        inputFormat="DD/MM/YYYY"
        disableAutoMonthSwitching
        animateYearScrolling
        renderInput={(startProps, endProps) => {
          return (
            <InputContainer withborder={String(withborder)}>
              <InputWrapperStart
                id="start-inputDate"
                ref={startProps.inputRef}
                startAdornment={
                  <DateText aria-hidden="true">{prefixName}</DateText>
                }
                withborder={String(withborder)}
                {...startProps}
              />
              <Delimiter>/</Delimiter>
              <InputWrapperEnd
                id="end-inputDate"
                ref={endProps.inputRef}
                endAdornment={
                  <AdormentRight position="end">
                    {endProps.focused ? (
                      <ArrowDropUpIcon />
                    ) : (
                      <ArrowDropDownIcon />
                    )}
                  </AdormentRight>
                }
                {...endProps}
              />
            </InputContainer>
          )
        }}
      />
    </LocalizationProvider>
  ),
  isEqualValue
)

InputRangeDate.displayName = 'InputRangeDate'
/**
 * InputRangeDate Component
 * 
 * A React component for selecting a range of dates. This component is typically
 * used in forms or filters where users need to specify a start and end date.
 * 
 * @component
 * @example
 * // Example usage:
 * <InputRangeDate
 *   startDate={new Date()}
 *   endDate={new Date()}
 *   onChange={(range) => console.log(range)}
 * />
 * 
 * @param {Object} props - The props for the InputRangeDate component.
 * @param {Date} props.startDate - The starting date of the range.
 * @param {Date} props.endDate - The ending date of the range.
 * @param {Function} props.onChange - Callback function triggered when the date range changes.
 * @param {string} [props.className] - Optional additional CSS class for styling.
 * 
 * @returns {JSX.Element} The rendered InputRangeDate component.
 */
export default InputRangeDate
